import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Clock, Loader2, Percent, Users, Calculator } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const ANNUAL_DISCOUNT = 15;
const TRIAL_DAYS = 15;

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  nurseries: any[];
  groups: any[];
}

const AddSubscriptionDialog = ({ open, onOpenChange, nurseries, groups }: Props) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [nurseryId, setNurseryId] = useState("");
  const [plan, setPlan] = useState("basic");
  const [amount, setAmount] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isTrial, setIsTrial] = useState(false);
  const [groupId, setGroupId] = useState("");
  const [pricingModel, setPricingModel] = useState<"fixed" | "per_child">("fixed");
  const [pricePerChild, setPricePerChild] = useState("10");
  const [minCharge, setMinCharge] = useState("500");

  const { data: childrenCount = 0 } = useQuery({
    queryKey: ["admin-nursery-children-count", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return 0;
      const { count } = await supabase
        .from("children")
        .select("*", { count: "exact", head: true })
        .eq("nursery_id", nurseryId);
      return count || 0;
    },
    enabled: !!nurseryId && pricingModel === "per_child",
  });

  const computed = useMemo(() => {
    if (isTrial) return { final: 0, discount: 0, discountType: "trial", original: 0 };

    let base: number;
    if (pricingModel === "per_child") {
      const ppc = parseFloat(pricePerChild) || 0;
      const mc = parseFloat(minCharge) || 0;
      base = Math.max(childrenCount * ppc, mc);
    } else {
      base = parseFloat(amount) || 0;
    }

    let discount = 0, discountType = "none";
    if (cycle === "yearly") { discount = ANNUAL_DISCOUNT; discountType = "annual"; }
    if (groupId) {
      const g = groups?.find((g: any) => g.id === groupId);
      if (g && g.discount_percent > discount) { discount = g.discount_percent; discountType = "group"; }
    }
    const yearlyBase = cycle === "yearly" ? base * 12 : base;
    return { final: Math.round(yearlyBase * (1 - discount / 100) * 100) / 100, discount, discountType, original: yearlyBase, monthlyBase: base };
  }, [amount, cycle, isTrial, groupId, groups, pricingModel, pricePerChild, minCharge, childrenCount]);

  const reset = () => {
    setNurseryId(""); setAmount(""); setNotes(""); setPaymentMethod("");
    setIsTrial(false); setGroupId(""); setExpiresAt(""); setPlan("basic"); setCycle("monthly");
    setPricingModel("fixed"); setPricePerChild("10"); setMinCharge("500");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nurseryId) return;
    setLoading(true);
    const now = new Date();
    const trialEnds = isTrial ? new Date(now.getTime() + TRIAL_DAYS * 86400000).toISOString() : null;

    const insertData: any = {
      nursery_id: nurseryId,
      plan: isTrial ? "free" : plan,
      amount: isTrial ? 0 : computed.final,
      original_amount: isTrial ? 0 : (computed.original || computed.monthlyBase || 0),
      billing_cycle: cycle,
      status: "active",
      is_trial: isTrial,
      trial_ends_at: trialEnds,
      expires_at: isTrial ? trialEnds : (expiresAt || null),
      discount_percent: isTrial ? 0 : computed.discount,
      discount_type: computed.discountType,
      group_id: groupId || null,
      notes: notes || null,
      payment_method: paymentMethod || null,
      pricing_model: pricingModel,
    };

    if (pricingModel === "per_child") {
      insertData.price_per_child = parseFloat(pricePerChild) || 10;
      insertData.min_charge = parseFloat(minCharge) || 500;
    }

    const { error } = await supabase.from("nursery_subscriptions").insert(insertData);
    setLoading(false);
    if (error) { toast.error("خطأ: " + error.message); return; }
    toast.success(isTrial ? `تم إنشاء فترة تجربة (${TRIAL_DAYS} يوم)` : "تم إنشاء الاشتراك بنجاح");
    queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    onOpenChange(false);
    reset();
  };

  const showPerChildBreakdown = pricingModel === "per_child" && !isTrial;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" dir="rtl">
        <DialogHeader><DialogTitle>إنشاء اشتراك جديد</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>الحضانة *</Label>
            <Select value={nurseryId} onValueChange={setNurseryId}>
              <SelectTrigger><SelectValue placeholder="اختر حضانة" /></SelectTrigger>
              <SelectContent>
                {nurseries?.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3 bg-blue-50/50 dark:bg-blue-950/20">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-sm font-medium">تجربة مجانية</p>
                <p className="text-xs text-muted-foreground">{TRIAL_DAYS} يوم مجاناً</p>
              </div>
            </div>
            <Switch checked={isTrial} onCheckedChange={setIsTrial} />
          </div>

          {!isTrial && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>الخطة</Label>
                  <Select value={plan} onValueChange={setPlan}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">مجاني</SelectItem>
                      <SelectItem value="basic">أساسي</SelectItem>
                      <SelectItem value="premium">متقدم</SelectItem>
                      <SelectItem value="enterprise">مؤسسي</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>نموذج التسعير</Label>
                  <Select value={pricingModel} onValueChange={(v) => setPricingModel(v as "fixed" | "per_child")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">ثابت (باقة)</SelectItem>
                      <SelectItem value="per_child">حسب عدد الأطفال</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {pricingModel === "fixed" ? (
                <div>
                  <Label>المبلغ الشهري (ج.م) *</Label>
                  <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required min={0} dir="ltr" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>سعر الطفل (ج.م)</Label>
                    <Input type="number" value={pricePerChild} onChange={(e) => setPricePerChild(e.target.value)} min={0} dir="ltr" />
                  </div>
                  <div>
                    <Label>الحد الأدنى (ج.م)</Label>
                    <Input type="number" value={minCharge} onChange={(e) => setMinCharge(e.target.value)} min={0} dir="ltr" />
                  </div>
                </div>
              )}

              {showPerChildBreakdown && nurseryId && (
                <div className="rounded-lg border p-3 bg-accent/30 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Calculator className="w-4 h-4" />
                    <span>حساب تلقائي</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>{childrenCount} طفل × {pricePerChild} ج.م = {(childrenCount * (parseFloat(pricePerChild) || 0)).toLocaleString()} ج.م</span>
                  </div>
                  {computed.monthlyBase !== undefined && computed.monthlyBase > childrenCount * (parseFloat(pricePerChild) || 0) && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      ⚠️ تم تطبيق الحد الأدنى ({parseFloat(minCharge).toLocaleString()} ج.م)
                    </p>
                  )}
                  <div className="text-sm font-bold border-t pt-1">
                    المبلغ الشهري: {computed.monthlyBase?.toLocaleString()} ج.م
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>دورة الفوترة</Label>
                  <Select value={cycle} onValueChange={setCycle}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">شهري</SelectItem>
                      <SelectItem value="yearly">سنوي (خصم {ANNUAL_DISCOUNT}%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>طريقة الدفع</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger><SelectValue placeholder="اختياري" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                      <SelectItem value="cash">نقدي</SelectItem>
                      <SelectItem value="online">إلكتروني</SelectItem>
                      <SelectItem value="card">بطاقة</SelectItem>
                      <SelectItem value="wallet">محفظة إلكترونية</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {groups && groups.length > 0 && (
                <div>
                  <Label>مجموعة خصم (اختياري)</Label>
                  <Select value={groupId} onValueChange={setGroupId}>
                    <SelectTrigger><SelectValue placeholder="بدون مجموعة" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">بدون مجموعة</SelectItem>
                      {groups.map((g: any) => <SelectItem key={g.id} value={g.id}>{g.name} ({g.discount_percent}%)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(pricingModel === "fixed" ? amount : true) && computed.original > 0 && (
                <div className="rounded-lg border p-3 bg-muted/30 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>المبلغ الأصلي</span>
                    <span>{computed.original.toLocaleString()} ج.م</span>
                  </div>
                  {computed.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span className="flex items-center gap-1"><Percent className="w-3 h-3" />خصم ({computed.discount}%)</span>
                      <span>- {(computed.original - computed.final).toLocaleString()} ج.م</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold border-t pt-1">
                    <span>المبلغ النهائي</span>
                    <span>{computed.final.toLocaleString()} ج.م</span>
                  </div>
                </div>
              )}
              <div>
                <Label>تاريخ الانتهاء</Label>
                <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </div>
            </>
          )}
          <div>
            <Label>ملاحظات</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ملاحظات إضافية..." rows={2} />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isTrial ? `بدء تجربة مجانية (${TRIAL_DAYS} يوم)` : "إنشاء الاشتراك"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSubscriptionDialog;
